import React, { useState, useEffect, useCallback } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { useSelector } from "react-redux";
import loader from "../../../../../components/recharts/loader.gif";
import {
  getSurplusOptimizerBatteryData,
  getSurplusOptimizerCapacityData,
  getSurplusharayanaSession,
} from "../../../../../Rest_api/RestApi";

interface CapacityOffer {
  start_date: string;
  end_date: string;
  source_type: string;
  id: number;
  source_id: number;
  plant_capacity: string;
  plf: string;
  variable_charge: string;
  fixed_charge: string;
  status?: boolean;
  offerType: "capacity";
}

interface BatteryOffer {
  start_date: string;
  end_date: string;
  id: number;
  battery_id: number;
  battery_capacity_in_mwh: number;
  chargingRate: string;
  vc: string;
  fc: string;
  status?: boolean;
  offerType: "battery";
}

interface ScenarioData {
  scenario_id: number;  // ID from backend
  scenario_number: number;
  is_finalized: boolean;
  capacities: CapacityOffer[];
  batteries: BatteryOffer[];
  selected: boolean;
}

interface PPEConsoleProps {
  onApply: (data: any) => void;
  onReset: () => void;
  onRefreshAllComponents?: () => void;
  loading: boolean;
  refreshKey: any;
  isSliderOpen: boolean;
  setRefreshKey: any;
  onActiveScenarioChange?: (scenario: number) => void;
  selectedScenarios?: Array<{ scenario_id: number; scenario_number: number }>;
}

export default function PPEConsole({
  onApply,
  onReset,
  onRefreshAllComponents,
  refreshKey,
  loading: applyLoading,
  isSliderOpen,
  setRefreshKey,
  onActiveScenarioChange,
  selectedScenarios,
}: PPEConsoleProps) {
  const [scenarios, setScenarios] = useState<ScenarioData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedScenarios, setExpandedScenarios] = useState<Set<number>>(new Set()); // All scenarios collapsed by default
  const [activeScenario, setActiveScenario] = useState<number>(0);

  const SelectedStartDate = useSelector(
    (state: any) => state.ProcurementOptimizerDemoDate.startDate
  );
  const SelectedEndDate = useSelector(
    (state: any) => state.ProcurementOptimizerDemoDate.endDate
  );

  // Fetch data for each scenario (scenario-specific)
  const fetchScenarios = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const sessionResponse = await getSurplusharayanaSession();



      if (sessionResponse?.scenarios) {
        // Fetch data for EACH scenario individually
        const scenariosWithData = await Promise.all(
          sessionResponse.scenarios.map(async (scenario: any) => {


            // Fetch capacity and battery data using scenario_id
            const [batteryData, capacityData] = await Promise.all([
              getSurplusOptimizerBatteryData(scenario.scenario_id),
              getSurplusOptimizerCapacityData(scenario.scenario_id),
            ]);



            // Format capacities for this scenario
            const formattedCapacities: CapacityOffer[] = (
              Array.isArray(capacityData) ? capacityData : []
            ).map((capacity: any) => ({
              id: capacity.id,
              source_id: capacity.source_id,
              start_date: capacity.start_date || "",
              end_date: capacity.end_date || "",
              source_type: capacity.type || capacity.source_type || "",
              plant_capacity: capacity.plant_capacity || "",
              plf: capacity.pf || capacity.plf || "",
              variable_charge: capacity.vc || capacity.variable_charge || "",
              fixed_charge: capacity.fc || capacity.fixed_charge || "",
              status: capacity.status ?? true,
              offerType: "capacity" as const,
            }));

            // Format batteries for this scenario
            const formattedBatteries: BatteryOffer[] = (
              Array.isArray(batteryData) ? batteryData : []
            ).map((battery: any) => ({
              id: battery.id,
              battery_id: battery.battery_id,
              start_date: battery.start_date || "",
              end_date: battery.end_date || "",
              chargingRate: battery.charge_discharge_rate_in_mw || "",
              battery_capacity_in_mwh: battery.battery_capacity_in_mwh || "",
              vc: battery.vc || battery.variable_charge || "",
              fc: battery.fc || battery.fixed_charge || "",
              status: battery.status ?? true,
              offerType: "battery" as const,
            }));

            return {
              scenario_id: scenario.scenario_id,
              scenario_number: scenario.scenario_number,
              is_finalized: scenario.is_finalized || false,
              capacities: formattedCapacities, // Specific to THIS scenario
              batteries: formattedBatteries,   // Specific to THIS scenario
              selected: selectedScenarios?.some(s => s.scenario_id === scenario.scenario_id) || false,
            };
          })
        );


        setScenarios(scenariosWithData);

        // Auto-select Scenario 0 if no scenarios are selected
        if ((!selectedScenarios || selectedScenarios.length === 0) && scenariosWithData.length > 0) {
          const defaultScenario = scenariosWithData.find(s => s.scenario_number === 0);

          if (defaultScenario) {
            // Update local state for immediate feedback (though setScenarios above might overlap, we need to ensure the parent knows)
            // Actually, we modified the object reference in the find() but that's in the array we just set. 
            // We should effectively update the state or just rely on calling onApply which will re-trigger fetch

            // Let's create the payload for onApply
            const selected = [defaultScenario]; // Only this one is selected
            const selectedScenarioIds = selected.map((s) => s.scenario_id);

            // We need to mark it as selected in the UI immediately too, to avoid flicker
            setScenarios(prev => prev.map(s => s.scenario_number === 0 ? { ...s, selected: true } : s));

            onApply({
              selectedScenarioIds,
              selectedScenarios: selected.map((s) => ({
                scenario_id: s.scenario_id,
                scenario_number: s.scenario_number,
              })),
              startDate: SelectedStartDate,
              endDate: SelectedEndDate,
            });
          }
        }

        if (sessionResponse.active_scenario?.scenario_number !== undefined) {
          setActiveScenario(sessionResponse.active_scenario.scenario_number);
          onActiveScenarioChange?.(sessionResponse.active_scenario.scenario_number);
        }
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch scenarios";
      setError(errorMessage);
      console.error("❌ Error:", err);
    } finally {
      setLoading(false);
    }
  }, [onActiveScenarioChange, selectedScenarios]);

  useEffect(() => {
    fetchScenarios();
  }, [fetchScenarios, refreshKey]);

  const handleScenarioToggle = (scenarioNumber: number) => {
    setExpandedScenarios((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(scenarioNumber)) {
        newSet.delete(scenarioNumber);
      } else {
        newSet.add(scenarioNumber);
      }
      return newSet;
    });
  };

  const handleScenarioSelection = (scenarioNumber: number) => {
    setScenarios((prev) =>
      prev.map((scenario) => {
        if (scenario.scenario_number === scenarioNumber) {
          const newSelected = !scenario.selected;

          // If turning ON, set as active scenario
          if (newSelected) {
            setActiveScenario(scenarioNumber);
            onActiveScenarioChange?.(scenarioNumber);
          }

          return { ...scenario, selected: newSelected };
        }
        return scenario;
      })
    );
  };

  const handleApply = async () => {
    setLoading(true);
    setError(null);
    try {
      const selectedScenarios = scenarios.filter((s) => s.selected);

      if (selectedScenarios.length === 0) {
        setError("Please select at least one scenario");
        return;
      }

      // Send scenario_id values of all selected scenarios
      const selectedScenarioIds = selectedScenarios.map((s) => s.scenario_id);

      onApply({
        selectedScenarioIds,  // Array of scenario_id values [320, 321, 322]
        selectedScenarios: selectedScenarios.map((s) => ({
          scenario_id: s.scenario_id,
          scenario_number: s.scenario_number,
        })),
        startDate: SelectedStartDate,
        endDate: SelectedEndDate,
      });

      setRefreshKey((prev: number) => prev + 1);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to apply changes";
      setError(errorMessage);
      console.error("Error applying:", err);
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="w-full h-full bg-[#FFF] flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto">
        {/* Header */}
        <div className="px-2 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-sm font-medium text-[#34656D]">
            Selected Scenarios
          </h2>
        </div>

        {loading && scenarios.length === 0 ? (
          <div className="h-full w-full bg-white flex items-center justify-center">
            <img
              src={loader}
              width={200}
              alt="loader"
              className="block mt-[5rem]"
            />
          </div>
        ) : error ? (
          <div className="px-2 py-8 text-center text-red-500">{error}</div>
        ) : scenarios.length === 0 ? (
          <div className="px-2 py-12 text-center">
            <p className="text-gray-500">No scenarios available</p>
          </div>
        ) : (
          <div className="px-2 py-2 space-y-2">
            {scenarios.map((scenario) => (
              <div
                key={scenario.scenario_number}
                className="border border-[#F1935C] rounded-xl overflow-hidden bg-white"
              >
                {/* Scenario Header */}
                <div
                  className={`flex items-center justify-between p-2 cursor-pointer ${expandedScenarios.has(scenario.scenario_number)
                    ? "bg-[#FFF5ED]"
                    : "bg-white hover:bg-gray-50"
                    }`}
                  onClick={() => handleScenarioToggle(scenario.scenario_number)}
                >
                  <div className="flex items-center gap-3">
                    {expandedScenarios.has(scenario.scenario_number) ? (
                      <ChevronUp size={20} className="text-[#F1935C]" />
                    ) : (
                      <ChevronDown size={20} className="text-[#F1935C]" />
                    )}
                    <span className="text-sm font-medium text-[#F1935C]">
                      Scenario {scenario.scenario_number}
                    </span>
                  </div>

                  {/* Toggle Switch */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleScenarioSelection(scenario.scenario_number);
                    }}
                    className={`relative inline-flex items-center h-5 w-9 rounded-full transition-colors focus:outline-none ${scenario.selected ? "bg-[#F1935C]" : "bg-[#DADCE0]"
                      }`}
                  >
                    <span
                      className={`inline-block w-4 h-4 transform bg-white rounded-full shadow-sm transition-transform ${scenario.selected ? "translate-x-4" : "translate-x-0.5"
                        }`}
                    />
                  </button>
                </div>

                {/* Expanded Content */}
                {expandedScenarios.has(scenario.scenario_number) && (
                  <div className="p-2 space-y-2 bg-white">
                    {/* Capacity Section - Always Visible */}
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="p-2 bg-[#F7FAFF]">
                        <h3 className="text-xs font-medium text-[#34656D]">
                          Capacity ({scenario.capacities.length})
                        </h3>
                      </div>

                      <div className="p-2 space-y-2 bg-white max-h-[300px] overflow-y-auto">
                        {scenario.capacities.length === 0 ? (
                          <p className="text-xs text-gray-500 text-center py-4">
                            No capacity added
                          </p>
                        ) : (
                          scenario.capacities.map((capacity) => (
                            <div
                              key={capacity.id}
                              className="border border-[#CAD5E2] rounded-lg p-2 bg-[#FFF]"
                            >
                              <div className="mb-2">
                                <span className="text-xs font-medium text-[#F1935C]">
                                  {capacity.source_type || "N/A"}
                                </span>
                              </div>
                              <div className="grid grid-cols-2 gap-3 text-xs">
                                <div>
                                  <div className="text-[#9AA0A6] mb-0.5">
                                    Start Date:
                                  </div>
                                  <div className="text-[#34656D] font-medium">
                                    {capacity.start_date || "N/A"}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-[#9AA0A6] mb-0.5">
                                    End Date:
                                  </div>
                                  <div className="text-[#34656D] font-medium">
                                    {capacity.end_date || "N/A"}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-[#9AA0A6] mb-0.5">PLF:</div>
                                  <div className="text-[#34656D] font-medium">
                                    {capacity.plf || "N/A"}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-[#9AA0A6] mb-0.5">FC:</div>
                                  <div className="text-[#34656D] font-medium">
                                    {capacity.fixed_charge || "N/A"}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-[#9AA0A6] mb-0.5">VC:</div>
                                  <div className="text-[#34656D] font-medium">
                                    {capacity.variable_charge || "N/A"}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-[#9AA0A6] mb-0.5">
                                    Quantum:
                                  </div>
                                  <div className="text-[#34656D] font-medium">
                                    {capacity.plant_capacity
                                      ? `${capacity.plant_capacity} MW`
                                      : "N/A"}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Battery Section - Always Visible */}
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="p-2 bg-[#F7FAFF]">
                        <h3 className="text-xs font-medium text-[#34656D]">
                          Battery ({scenario.batteries.length})
                        </h3>
                      </div>

                      <div className="p-2 space-y-2 bg-white max-h-[300px] overflow-y-auto">
                        {scenario.batteries.length === 0 ? (
                          <p className="text-xs text-gray-500 text-center py-4">
                            No battery added
                          </p>
                        ) : (
                          scenario.batteries.map((battery) => (
                            <div
                              key={battery.id}
                              className="border border-[#CAD5E2] rounded-lg p-2 bg-[#FFF]"
                            >
                              <div className="mb-2">
                                <span className="text-xs font-medium text-[#5B8A9F]">
                                  BS {battery.battery_id}
                                </span>
                              </div>
                              <div className="grid grid-cols-2 gap-3 text-xs">
                                <div>
                                  <div className="text-[#9AA0A6] mb-0.5">
                                    Start Date:
                                  </div>
                                  <div className="text-[#34656D] font-medium">
                                    {battery.start_date || "N/A"}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-[#9AA0A6] mb-0.5">
                                    End Date:
                                  </div>
                                  <div className="text-[#34656D] font-medium">
                                    {battery.end_date || "N/A"}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-[#9AA0A6] mb-0.5">
                                    Quantum:
                                  </div>
                                  <div className="text-[#34656D] font-medium">
                                    {battery.battery_capacity_in_mwh
                                      ? `${battery.battery_capacity_in_mwh} MWh`
                                      : "N/A"}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-[#9AA0A6] mb-0.5">
                                    Charge Rate:
                                  </div>
                                  <div className="text-[#34656D] font-medium">
                                    {battery.chargingRate || "N/A"}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-[#9AA0A6] mb-0.5">FC:</div>
                                  <div className="text-[#34656D] font-medium">
                                    {battery.fc || "N/A"}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-[#9AA0A6] mb-0.5">VC:</div>
                                  <div className="text-[#34656D] font-medium">
                                    {battery.vc || "N/A"}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Apply Button - Fixed at Bottom */}
      <div className="bg-white border-t border-gray-200 p-2">
        <button
          onClick={handleApply}
          disabled={applyLoading || loading || scenarios.filter(s => s.selected).length === 0}
          className={`w-full h-[36px] text-sm font-medium rounded-lg cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${applyLoading || loading || scenarios.filter(s => s.selected).length === 0
            ? "bg-gray-100 text-gray-400 border border-gray-200 shadow-none"
            : "text-white bg-[#F1935C] shadow-md shadow-orange-500/20 hover:bg-[#cd733f]"
            }`}
        >
          {applyLoading || loading ? "Loading..." : "Apply"}
        </button>
      </div>

      {error && (
        <div className="px-2 pb-3 bg-white border-t border-gray-200">
          <p className="text-xs text-red-500 text-center">{error}</p>
        </div>
      )}
    </div>
  );
}
